import { IStringTool } from "../core/IStringTool";

/**
 * Input for parsing unit-based strings into numeric pixel values.
 */
interface UnitParserInput {
  /** Unit string, e.g. `"20px"`, `"50%"`, `"1.5rem"`, or `"selfHeight"` */
  value: string;

  /** DOM element used for `"selfHeight"` calculation */
  element: HTMLElement;

  /** Viewport height in pixels (for percentage conversion) */
  viewportHeight: number;

  /** Root font size in pixels (for rem conversion) */
  baseRem: number;

  boundingRect: DOMRect;
}

/**
 * Converts unit-based strings to numeric pixel values.
 * Supports `px`, `%`, `rem`, and `"selfHeight"` keyword. Handles negatives.
 */
export default class UnitParserTool implements IStringTool<UnitParserInput, number> {
  /**
   * @returns Numeric value in pixels (positive or negative).
   */
  process({ value, element, viewportHeight, baseRem, boundingRect }: UnitParserInput): number {
    const parts = value
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);

    let sum = 0;
    for (const part of parts) {
      let v = part;
      let isNegative = false;
      if (v.startsWith("-")) {
        isNegative = true;
        v = v.slice(1);
      }
      let result = 0;
      if (v === "selfHeight") {
        result = element.offsetHeight;
      } else if (v.endsWith("px")) {
        result = parseFloat(v);
      } else if (v.endsWith("%")) {
        result = (parseFloat(v) / 100) * viewportHeight;
      } else if (v.endsWith("rem")) {
        result = parseFloat(v) * baseRem;
      } else if (v.endsWith("sh")) {
        result = (parseFloat(v) * boundingRect.height) / 100;
      } else {
        result = parseFloat(v);
      }

      sum += isNegative ? -result : result;
    }

    return sum;
  }
}
